import { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { useSettings } from '../context/SettingsContext';
import { toast } from 'sonner';
import axios from 'axios';
import { Upload, ArrowLeft, Loader2, CheckCircle, Lock } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ResetPassword = () => {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [loading, setLoading] = useState(false);
    const [success, setSuccess] = useState(false);
    const { settings } = useSettings();

    const token = searchParams.get('token');
    const businessName = settings?.business_name || 'BGRemove';
    const logoUrl = settings?.logo_url;

    useEffect(() => {
        if (!token) {
            toast.error('Invalid or missing reset token');
            navigate('/forgot-password');
        }
    }, [token, navigate]);

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!password || !confirmPassword) {
            toast.error('Please fill in all fields');
            return;
        }

        if (password.length < 6) {
            toast.error('Password must be at least 6 characters');
            return;
        }

        if (password !== confirmPassword) {
            toast.error('Passwords do not match');
            return;
        }

        setLoading(true);
        try {
            await axios.post(`${API}/auth/reset-password`, {
                token,
                new_password: password
            });
            setSuccess(true);
            toast.success('Password reset successfully!');
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to reset password');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen bg-background flex">
            {/* Grain overlay */}
            <div className="grain-overlay" />

            {/* Left side - Brand */}
            <div className="hidden lg:flex lg:w-1/2 bg-foreground text-background p-12 flex-col justify-between">
                <Link to="/" className="flex items-center gap-2">
                    {logoUrl ? (
                        <img
                            src={logoUrl.startsWith('http') ? logoUrl : `${process.env.REACT_APP_BACKEND_URL}${logoUrl}`}
                            alt={businessName}
                            className="w-8 h-8 rounded-lg object-contain"
                        />
                    ) : (
                        <div className="w-8 h-8 rounded-lg bg-primary flex items-center justify-center">
                            <Upload className="w-4 h-4 text-primary-foreground" />
                        </div>
                    )}
                    <span className="font-heading font-bold text-xl tracking-tight">{businessName}</span>
                </Link>

                <div>
                    <h2 className="font-heading font-bold text-4xl tracking-tight mb-4">
                        Create New Password
                    </h2>
                    <p className="text-background/70 text-lg">
                        Your new password must be different from previously used passwords.
                    </p>
                </div>

                <p className="text-sm text-background/50">
                    © 2024 {businessName}. All rights reserved.
                </p>
            </div>

            {/* Right side - Form */}
            <div className="w-full lg:w-1/2 flex items-center justify-center p-8">
                <div className="w-full max-w-md">
                    <Link
                        to="/login"
                        className="inline-flex items-center text-sm text-muted-foreground hover:text-foreground mb-8 transition-colors"
                    >
                        <ArrowLeft className="w-4 h-4 mr-2" />
                        Back to login
                    </Link>

                    <div className="lg:hidden mb-8">
                        <Link to="/" className="flex items-center gap-2">
                            {logoUrl ? (
                                <img
                                    src={logoUrl.startsWith('http') ? logoUrl : `${process.env.REACT_APP_BACKEND_URL}${logoUrl}`}
                                    alt={businessName}
                                    className="w-8 h-8 rounded-lg object-contain"
                                />
                            ) : (
                                <div className="w-8 h-8 rounded-lg bg-primary flex items-center justify-center">
                                    <Upload className="w-4 h-4 text-primary-foreground" />
                                </div>
                            )}
                            <span className="font-heading font-bold text-xl tracking-tight">{businessName}</span>
                        </Link>
                    </div>

                    {!success ? (
                        <>
                            <h1 className="font-heading font-bold text-3xl tracking-tight mb-2">
                                Reset password
                            </h1>
                            <p className="text-muted-foreground mb-8">
                                Enter your new password below
                            </p>

                            <form onSubmit={handleSubmit} className="space-y-6">
                                <div className="space-y-2">
                                    <Label htmlFor="password">New Password</Label>
                                    <Input
                                        id="password"
                                        type="password"
                                        placeholder="Minimum 6 characters"
                                        value={password}
                                        onChange={(e) => setPassword(e.target.value)}
                                        className="h-12"
                                        autoFocus
                                    />
                                </div>

                                <div className="space-y-2">
                                    <Label htmlFor="confirmPassword">Confirm Password</Label>
                                    <Input
                                        id="confirmPassword"
                                        type="password"
                                        placeholder="Re-enter your password"
                                        value={confirmPassword}
                                        onChange={(e) => setConfirmPassword(e.target.value)}
                                        className="h-12"
                                    />
                                </div>

                                <Button
                                    type="submit"
                                    className="w-full h-12 rounded-full text-base"
                                    disabled={loading}
                                >
                                    {loading ? (
                                        <>
                                            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                            Resetting password...
                                        </>
                                    ) : (
                                        <>
                                            <Lock className="mr-2 h-4 w-4" />
                                            Reset password
                                        </>
                                    )}
                                </Button>
                            </form>
                        </>
                    ) : (
                        <div className="text-center py-8">
                            <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-4">
                                <CheckCircle className="w-8 h-8 text-primary" />
                            </div>
                            <h1 className="font-heading font-bold text-2xl tracking-tight mb-2">
                                Password reset!
                            </h1>
                            <p className="text-muted-foreground mb-8">
                                Your password has been successfully reset. You can now sign in with your new password.
                            </p>
                            <Button
                                onClick={() => navigate('/login')}
                                className="rounded-full w-full"
                            >
                                Continue to login
                            </Button>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default ResetPassword;
